import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
    buildCustomHandleMaterial,
    clamp01,
    normalizeHexColor,
    readHandleMapFileAsDataUrl,
} from "./handleMaterials";

const COLOR_SWATCHES = [
    "#C8CDD2",
    "#A8AEB4",
    "#2A2A2A",
    "#C4A35A",
    "#D4AF37",
    "#F2F2F0",
    "#8D6E63",
    "#616161",
    "#1565C0",
    "#C62828",
    "#FFFFFF",
    "#B0BEC5",
];

const sliderRowSx = {
    display: "grid",
    gridTemplateColumns: "100px 1fr 44px",
    alignItems: "center",
    gap: 1,
    mb: 0.5,
};

/**
 * Создание / редактирование материала ручки (цвет + PBR + опциональная картинка).
 * onLivePreview — сразу на сцену (как у текстур).
 */
const HandleMaterialDialog = ({
    open,
    onClose,
    onConfirm,
    onLivePreview,
    initialValues = null,
}) => {
    const isEdit = Boolean(initialValues?.id);
    const fileInputRef = useRef(null);

    const [name, setName] = useState("");
    const [color, setColor] = useState("#C8CDD2");
    const [metalness, setMetalness] = useState(0.5);
    const [roughness, setRoughness] = useState(0.4);
    const [mapUrl, setMapUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setName(String(initialValues?.name || "").trim());
        setColor(normalizeHexColor(initialValues?.color, "#C8CDD2"));
        setMetalness(clamp01(initialValues?.metalness, 0.5));
        setRoughness(clamp01(initialValues?.roughness, 0.4));
        setMapUrl(initialValues?.mapUrl || null);
        setBusy(false);
        setError("");
    }, [open, initialValues]);

    // Живое превью на сцене
    useEffect(() => {
        if (!open) {
            onLivePreview?.(null);
            return undefined;
        }
        onLivePreview?.({
            mode: isEdit ? "edit" : "create",
            materialId: isEdit ? initialValues?.id : null,
            props: {
                id: isEdit ? initialValues?.id : "preview",
                fromFile: false,
                color: normalizeHexColor(color, "#C8CDD2"),
                metalness: clamp01(metalness, 0.5),
                roughness: clamp01(roughness, 0.4),
                mapUrl: mapUrl || null,
            },
        });
        return undefined;
    }, [open, isEdit, initialValues?.id, color, metalness, roughness, mapUrl, onLivePreview]);

    const handleClose = () => {
        if (busy) return;
        onLivePreview?.(null);
        onClose?.();
    };

    const onPickMap = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setBusy(true);
        setError("");
        try {
            const url = await readHandleMapFileAsDataUrl(file);
            setMapUrl(url);
        } catch (err) {
            setError(err?.message || "Не удалось загрузить картинку");
        } finally {
            setBusy(false);
        }
    };

    const handleConfirm = () => {
        const record = buildCustomHandleMaterial({
            id: isEdit ? initialValues.id : undefined,
            name: name || (isEdit ? initialValues.name : "Свой"),
            color,
            metalness,
            roughness,
            mapUrl,
        });
        onLivePreview?.(null);
        onConfirm?.(
            {
                ...record,
                builtin: isEdit ? Boolean(initialValues?.builtin) : false,
            },
            {isEdit},
        );
        onClose?.();
    };

    return (
        <Dialog
            open={Boolean(open)}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            disableScrollLock
            disableRestoreFocus
            hideBackdrop
            sx={{
                pointerEvents: "none",
                "& .MuiDialog-container": {
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    pt: {xs: 2, md: 3},
                    pl: {xs: 1, md: 2},
                    pointerEvents: "none",
                },
                "& .MuiPaper-root": {
                    pointerEvents: "auto",
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: "10px",
                    width: "100%",
                    maxWidth: 400,
                    m: 1,
                    maxHeight: "min(92vh, 640px)",
                    position: "relative",
                },
            }}
        >
            <DialogTitle sx={{pb: 0.5, textAlign: "center", fontSize: "1.05rem"}}>
                {isEdit ? "Редактировать материал ручки" : "Свой материал ручки"}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: 1.5, pt: 1}}>
                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.4}}>
                    Смотрите на ручку справа на сцене — цвет, металл и матовость меняются сразу.
                </Typography>
                <TextField
                    label="Название"
                    size="small"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={busy}
                    placeholder="Например: Хром тёмный"
                />

                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 0.75}}>
                        Цвет
                    </Typography>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "6px",
                                border: "1px solid rgba(0,0,0,0.2)",
                                background: `linear-gradient(145deg, ${color} 0%, rgba(255,255,255,0.5) 45%, ${color} 100%)`,
                                flexShrink: 0,
                            }}
                        />
                        <TextField
                            size="small"
                            value={color}
                            onChange={(e) => setColor(normalizeHexColor(e.target.value, color))}
                            disabled={busy}
                            sx={{width: 120}}
                            inputProps={{maxLength: 7}}
                        />
                        <Box
                            component="input"
                            type="color"
                            value={color}
                            disabled={busy}
                            onChange={(e) => setColor(normalizeHexColor(e.target.value, color))}
                            sx={{
                                width: 40,
                                height: 36,
                                border: "none",
                                padding: 0,
                                background: "transparent",
                                cursor: busy ? "default" : "pointer",
                            }}
                        />
                    </Box>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {COLOR_SWATCHES.map((swatch) => (
                            <Box
                                key={swatch}
                                component="button"
                                type="button"
                                disabled={busy}
                                onClick={() => setColor(swatch)}
                                title={swatch}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "4px",
                                    border: color.toUpperCase() === swatch.toUpperCase()
                                        ? "2px solid"
                                        : "1px solid rgba(0,0,0,0.2)",
                                    borderColor: color.toUpperCase() === swatch.toUpperCase()
                                        ? "primary.main"
                                        : undefined,
                                    backgroundColor: swatch,
                                    cursor: busy ? "default" : "pointer",
                                    p: 0,
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                <Box>
                    <Box sx={sliderRowSx}>
                        <Typography variant="body2" color="text.secondary">Металл</Typography>
                        <Slider
                            size="small"
                            min={0}
                            max={1}
                            step={0.01}
                            value={metalness}
                            disabled={busy}
                            onChange={(_, v) => setMetalness(clamp01(v, 0.5))}
                        />
                        <Typography variant="caption">{Math.round(metalness * 100)}%</Typography>
                    </Box>
                    <Box sx={sliderRowSx}>
                        <Typography variant="body2" color="text.secondary">Матовость</Typography>
                        <Slider
                            size="small"
                            min={0}
                            max={1}
                            step={0.01}
                            value={roughness}
                            disabled={busy}
                            onChange={(_, v) => setRoughness(clamp01(v, 0.4))}
                        />
                        <Typography variant="caption">{Math.round(roughness * 100)}%</Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 0.75}}>
                        Картинка покрытия (необязательно)
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                        hidden
                        onChange={onPickMap}
                    />
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {mapUrl ? "Заменить" : "Загрузить"}
                        </Button>
                        {mapUrl && (
                            <Button
                                size="small"
                                color="inherit"
                                disabled={busy}
                                onClick={() => setMapUrl(null)}
                            >
                                Убрать
                            </Button>
                        )}
                    </Box>
                    {mapUrl && (
                        <Box
                            component="img"
                            src={mapUrl}
                            alt="Покрытие"
                            sx={{
                                mt: 1,
                                width: 96,
                                height: 96,
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "1px solid rgba(0,0,0,0.15)",
                            }}
                        />
                    )}
                </Box>

                {error && (
                    <Typography color="error" variant="body2">{error}</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={busy} color="inherit">
                    Отмена
                </Button>
                <Button variant="contained" onClick={handleConfirm} disabled={busy}>
                    {isEdit ? "Сохранить" : "Добавить"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HandleMaterialDialog;
